// src/lib/commission-lignes.js — LA commission d'un mois, ligne par ligne,
// arrondie au centime. UNE seule source, lue par la facture ET par l'écran.
//
// ⚠️ CHEMIN ARGENT. Module PUR : aucune dépendance Supabase/React.
//
// ── Le défaut (mesuré le 2026-09-30) ─────────────────────────────────────
// Le Havre de Bruguières, septembre : l'écran Facturation affichait
// 1 083,14 €, le PDF de la facture 1 083,15 €. Deux calculs :
//   - la facture arrondit la commission de CHAQUE réservation au centime,
//     puis additionne ces montants (correctif du 2026-08-05, pour que la
//     colonne « Comm. » s'additionne à la calculatrice) ;
//   - l'écran appliquait le taux au TOTAL des bases, sans arrondi par ligne.
// Σ(arrondis) ≠ arrondi(Σ) : un centime d'écart, parfois deux.
//
// ── Le remède ────────────────────────────────────────────────────────────
// Le calcul par ligne quitte le gabarit de la facture et vit ICI. La facture
// l'appelle (rendu inchangé — c'est un déplacement, pas une réécriture) ;
// `calc()` (src/lib/calc.js ET son miroir lib/calc.js) l'appelle aussi — donc
// TOUS les écrans qui lisent calc().comm : Facturation, tableau de bord,
// Annuel, Compta, Statistiques admin, rapport propriétaire, revenu FBZ lu par
// l'opérateur, rapport mensuel. Plus de deuxième formule à tenir à jour.
//
// ⚠️ MIROIR CJS : lib/commission-lignes.js (ADR-021), parité testée.

// Arrondi au centime. NaN reste NaN (un calcul raté ne devient jamais 0,00 €).
const round2 = (n) => {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? Math.round(v * 100) / 100 : NaN;
};

/**
 * Taux d'une réservation, EXACTEMENT comme la facture l'imprime.
 * @param {object} baremeSrc - barème en vigueur (logement, ou barème daté)
 * @param {number} tauxFixe  - taux appliqué en mode non dégressif
 * @param {number} nuits
 */
export function tauxLigneFacture(baremeSrc, tauxFixe, nuits) {
  if (baremeSrc && baremeSrc.commissionMode === "degressive") {
    return (
      [...(baremeSrc.paliers || [])]
        .sort((a, b) => a.maxNuits - b.maxNuits)
        .find((p) => (nuits || 0) <= p.maxNuits)?.taux ??
      baremeSrc.tauxBase ??
      baremeSrc.taux
    );
  }
  return tauxFixe;
}

/**
 * LA commission d'UNE réservation, arrondie au centime — la règle unique.
 * Base = montant − ménage (taxe trackée séparément, cf. ADR-048).
 * @returns {{ nuitee: number, comm: number }}
 */
export function commissionLigneFacture(r, { baremeSrc, tauxFixe }) {
  const nuitee = Math.max(0, ((r && r.montant) || 0) - ((r && r.menage) || 0));
  const tauxR = tauxLigneFacture(baremeSrc, tauxFixe, r && r.nuits);
  return { nuitee, comm: round2((nuitee * tauxR) / 100) };
}

/**
 * Les lignes de la facture : triées par date d'arrivée, commission arrondie
 * au centime sur CHAQUE ligne.
 * @returns {Array<{ r, nuitee, comm, totalFbz }>}
 */
export function lignesCommissionFacture(resas, { baremeSrc, tauxFixe }) {
  return [...(resas || [])]
    .filter(Boolean)
    .sort((a, b) =>
      (a.checkIn || a.date || "").localeCompare(b.checkIn || b.date || ""),
    )
    .map((r) => {
      const { nuitee, comm } = commissionLigneFacture(r, { baremeSrc, tauxFixe });
      return { r, nuitee, comm, totalFbz: round2(comm + (r.menage || 0)) };
    });
}

/** Σ des commissions ARRONDIES ligne par ligne (le total de la facture). */
export function commissionTotaleFacture(lignes) {
  return round2((lignes || []).reduce((s, l) => s + l.comm, 0));
}
